import {useCallback, useEffect, useState} from 'react';
import {QueryKey, useMutation, useQuery} from 'react-query';
import {useInterval} from 'react-use';
import {Upload, UploadOptions} from 'tus-js-client';

import {VideoUploadData} from 'client/common/utils/api-client/types';
import {useSurveySlug} from 'client/common/contexts/slug';

import {
    createResource,
    fetchResource,
    getVideoUploadLink,
    getVHVideoInfo
} from 'client/common/utils/api-client';

import {VHVideoState} from '../types';

const statusesForIntervalCheck: VHVideoState[] = ['new', 'transcoding', 'moderating'];

interface UseCreateResourceProps {
    type: 'vh_video';
    resourceData: {
        videoMetaId: string;
        videoFileId: string;
    };
    clientId?: string;
}

export function useCreateResource() {
    const {mutateAsync} = useMutation(createResource);
    const slug = useSurveySlug();

    return useCallback(
        async (params: UseCreateResourceProps) => {
            return mutateAsync({...params, slug});
        },
        [mutateAsync, slug]
    );
}

const RESOURCES_KEY = 'resources';

export function getResourcesKey(id?: string): QueryKey {
    return [RESOURCES_KEY, id];
}

export function useResource(resourceId?: string) {
    return useQuery(getResourcesKey(resourceId), async () => {
        if (!resourceId) {
            return;
        }

        const {data} = await fetchResource({id: resourceId});

        return data;
    });
}

export function useCheckForShortId(resourceId?: string) {
    const [shortId, setShortId] = useState<string>();
    const [delay, setDelay] = useState<null | number>(10000);

    const fetchShortId = useCallback(async () => {
        if (!resourceId) return;

        const {data} = await fetchResource({id: resourceId});

        if ('error' in data) {
            return;
        }

        if (data?.resourceData?.shortId) {
            setShortId(data.resourceData.shortId);
            setDelay(null);
        }

        return data;
    }, [resourceId]);

    useInterval(() => {
        void fetchShortId();
    }, delay);

    useEffect(() => {
        setDelay(10000);
        void fetchShortId();
    }, [fetchShortId]);

    return shortId;
}

export function useCheckVHVideoState(resourceId?: string, initialState: VHVideoState = 'ready') {
    const [state, setState] = useState<VHVideoState>(initialState);

    const fetchState = useCallback(async () => {
        if (!resourceId) return;

        const {data} = await fetchResource({id: resourceId});

        if ('error' in data) {
            return;
        }

        if (data?.resourceData?.state) {
            setState(data.resourceData.state);
        }

        return data;
    }, [resourceId]);

    useInterval(() => {
        if (state && statusesForIntervalCheck.includes(state)) {
            void fetchState();
        }
    }, 30000);

    useEffect(() => {
        setState(initialState);
    }, [initialState]);

    return state;
}

export function useGetVideoUploadLink() {
    const {mutateAsync} = useMutation(getVideoUploadLink);
    const slug = useSurveySlug();

    return useCallback(
        async (file: File, clientId?: string) => {
            return mutateAsync({name: file.name, length: file.size, clientId, slug});
        },
        [mutateAsync, slug]
    );
}

export function useGetVHVideoInfo() {
    const {mutateAsync} = useMutation(getVHVideoInfo);

    return useCallback(
        async videoMetaId => {
            return mutateAsync({videoMetaId});
        },
        [mutateAsync]
    );
}

export function useUploadVideo() {
    const getUploadLink = useGetVideoUploadLink();

    const uploadVideo = useCallback(
        async (
            file: File,
            clientId?: string,
            options?: Pick<UploadOptions, 'onError' | 'onProgress'> & {
                onSuccess: (data: VideoUploadData) => void;
            }
        ) => {
            const {onError, onProgress, onSuccess} = options || {};

            const {status, data} = await getUploadLink(file, clientId);

            if (status === 201 && 'location' in data) {
                const upload = new Upload(file, {
                    endpoint: data.location,
                    uploadUrl: data.location,
                    retryDelays: [],
                    chunkSize: 5242880,
                    metadata: {
                        filename: file.name,
                        filetype: file.type
                    },
                    onError,
                    onProgress,
                    onSuccess: () => {
                        if (onSuccess) {
                            onSuccess(data);
                        }
                    }
                });

                upload.start();

                return upload;
            }

            if (onError && 'error' in data) {
                onError(new Error(data.error));
            }

            console.error(data);
        },
        [getUploadLink]
    );

    return uploadVideo;
}
